import { BaseComponent } from './base-component.js';
import { customElement, property, query } from 'lit/decorators.js';
import { html, css, CSSResultGroup, TemplateResult } from 'lit';

@customElement('il-section')
export default class SectionComponent extends BaseComponent {
  @property({ type: String }) header?: string;
  @property({ type: Boolean }) divider = false;

  @query('slot[name="footer"]') private footerSlot?: HTMLSlotElement

  get shouldRenderFooter(): boolean {
    return Boolean(this.footerSlot?.assignedElements({ flatten: true }).length)
  }

  _onFooterSlotChange() {
    this.requestUpdate()
  }

  dividerElement(): TemplateResult {
    if (this.divider) {
      return html`<hr class="il-section__divider" />`;
    }

    return html``;
  }

  headerElement(): TemplateResult {
    if (this.header) {
      return html`<div class="il-section__header" data-test-iliad-il-section-header>
        <div class="il-section__header-left">
          <slot name="header-left"></slot>
          <span class="il-section__header-text">${this.header}</span>
        </div>
        <slot name="header-right"></slot>
      </div>`;
    }

    return html``;
  }

  override render() {
    return html`
      <div
        class="
            il-component
            il-section
            ${this.shouldRenderFooter ? 'il-section--with-footer' : ''}
        "
        data-test-iliad-il-section part="section"
      >
        <slot name="before"></slot>
        <div class="il-section__content" part="section-content" data-test-iliad-il-section-content>
          ${this.headerElement()}
          ${this.dividerElement()}
          <slot name="content" data-test-iliad-il-section-content></slot>
        </div>

        <div class="il-section__footer" data-test-iliad-il-section-footer>
          <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
        </div>
      </div>
    `;
  }

  static override styles = [
    BaseComponent.styles,
    css`
      .il-section {
        display: flex;
        flex-direction: column;
        gap: 16px;

        background-color: var(--il-color-neutrals-0);
        box-shadow: var(--il-shadow-cool-base);

        max-height: 100%;
      }

      .il-section__content {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
      }

      .il-section--with-footer .il-section__content {
        padding-bottom: 0;
      }

      .il-section__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
      }

      .il-section__header-left {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .il-section__header-text {
        font-size: var(--il-font-size-20);
        font-weight: var(--il-font-weight-xbold);
        line-height: var(--il-font-line-height-125);
        letter-spacing: var(--il-font-letter-spacing-2);
        color: var(--il-color-neutrals-350);
      }

      .il-section__divider {
        margin: 0;
        border: none;
        border-bottom: 1px solid var(--il-color-neutrals-100);
      }

      .il-section__footer {
        display: none;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        padding-top: 0;
      }

      .il-section--with-footer .il-section__footer {
        display: flex;
      }
    `,
  ] as CSSResultGroup;
}
